<template>
  <!--
    TASK-SF-07 -- DESIGN-09, onboarding step 3 of 3 (optional details).
    Both fields are optional; the primary is always enabled. On
    EMAIL_CONFLICT the wizard HOLDS here with every value intact -- the
    parent passes `emailError` and this step maps it to the email field and
    offers "بدون ایمیل ادامه بده".

    HUMAN DECISION 3: the inline terms sentence was removed. Consent is now
    stated once, in the student-onboarding shell footnote (outside the card),
    where the قوانین/حریم link also lives. `terms: true` is still sent by the
    terminal call -- the implicit-consent behaviour is unchanged.
  -->
  <form
    class="step-optional"
    @submit.prevent="onSubmit"
    data-testid="step-optional"
  >
    <p class="onboarding-eyebrow" data-testid="onboarding-eyebrow">
      {{ $t('login.studentOnboarding.eyebrow') }}
    </p>
    <h1 ref="heading" tabindex="-1" class="uc-form-header">
      {{ $t('login.studentOnboarding.step3.heading') }}
    </h1>
    <p class="uc-form-lead">{{ $t('login.studentOnboarding.step3.lead') }}</p>

    <div class="uc-form-element">
      <label for="onboardingSchoolName" class="uc-form-label">
        {{ $t('login.studentOnboarding.step3.schoolLabel') }}
        <span class="onboarding-optional-marker">{{
          $t('login.studentOnboarding.optionalMarker')
        }}</span>
      </label>
      <input
        id="onboardingSchoolName"
        class="uc-form-text-input"
        type="text"
        :placeholder="$t('login.studentOnboarding.step3.schoolPlaceholder')"
        data-testid="onboarding-school-name"
        :maxlength="freeTextMax"
        :value="value.schoolName"
        @input="update('schoolName', $event.target.value)"
      />
    </div>

    <div class="uc-form-element">
      <label for="onboardingEmail" class="uc-form-label">
        {{ $t('login.studentOnboarding.step3.emailLabel') }}
        <span class="onboarding-optional-marker">{{
          $t('login.studentOnboarding.optionalMarker')
        }}</span>
      </label>
      <input
        id="onboardingEmail"
        class="uc-form-text-input email-input"
        type="email"
        dir="ltr"
        inputmode="email"
        autocomplete="email"
        placeholder="name@example.com"
        data-testid="onboarding-email"
        :aria-invalid="emailFieldError ? 'true' : null"
        :aria-describedby="emailFieldError ? 'onboardingEmailError' : null"
        :value="value.email"
        @input="update('email', $event.target.value)"
      />
      <p
        v-if="emailFieldError"
        id="onboardingEmailError"
        class="uc-form-error"
        role="alert"
        data-testid="onboarding-email-error"
      >
        {{ emailFieldError }}
      </p>
      <button
        v-if="emailError"
        type="button"
        class="uc-link-button"
        data-testid="onboarding-continue-without-email"
        @click="continueWithoutEmail"
      >
        {{ $t('login.studentOnboarding.step3.continueWithoutEmail') }}
      </button>
    </div>

    <div class="onboarding-actions">
      <button
        type="button"
        class="onboarding-actions__back"
        data-testid="onboarding-step3-back"
        :disabled="submitting"
        @click="$emit('back')"
      >
        {{ $t('login.studentOnboarding.previousButton') }}
      </button>
      <button
        class="uc-form-button"
        type="submit"
        data-testid="onboarding-step3-submit"
        :disabled="submitting"
      >
        <span
          v-if="submitting"
          class="uc-form-spinner"
          aria-hidden="true"
        ></span>
        {{ $t('login.studentOnboarding.step3.submitButton') }}
      </button>
    </div>
  </form>
</template>

<script>
import Validator from 'validator'
import { STUDENT_FREE_TEXT_MAX } from '@/utils/student-profile-limits'

export default {
  name: 'StepOptional',
  props: {
    value: {
      type: Object,
      default: () => ({ schoolName: '', email: '' }),
    },
    // Server-side EMAIL_CONFLICT message from the terminal registration call.
    emailError: { type: String, default: null },
    submitting: { type: Boolean, default: false },
  },
  data() {
    return { localEmailError: null, freeTextMax: STUDENT_FREE_TEXT_MAX }
  },
  computed: {
    emailFieldError() {
      return this.emailError || this.localEmailError
    },
  },
  watch: {
    // A fresh keystroke clears only the CLIENT-side format error; the
    // server conflict error is owned by the parent and cleared on resubmit.
    'value.email'() {
      this.localEmailError = null
    },
  },
  mounted() {
    if (this.$refs.heading) this.$refs.heading.focus()
  },
  methods: {
    update(key, val) {
      this.$emit('input', { [key]: val })
    },
    continueWithoutEmail() {
      this.$emit('input', { email: '' })
      this.$emit('clear-email-conflict')
      this.$nextTick(() => this.onSubmit())
    },
    onSubmit() {
      if (this.submitting) return
      const email = (this.value.email || '').trim()
      if (email && !Validator.isEmail(email)) {
        this.localEmailError = this.$t(
          'login.studentOnboarding.step3.emailFormatError'
        )
        return
      }
      this.$emit('submit')
    },
  },
}
</script>
